import * as THREE from 'three'

import { readFileText } from '@core/lib/readFile'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  type CenaIfcx,
  type FicheiroIfcx,
  type Malha,
  compor,
  elementoDoNo,
  lerCena,
  pareceIfcx,
  raizes,
} from '@viewer/ifcx/ifcxParse'

/** Abrir e desenhar IFC5 (`.ifcx`).
 *
 *  Porque é que isto vive à parte e não entra pelo mesmo cano dos IFC
 *  de sempre: o IFC5 não passa pelo web-ifc nem pelos Fragments. É
 *  JSON, a geometria vem em malhas explícitas, e o formato está em
 *  alfa. Metê-lo na lista de modelos fazia-o parecer o que não é, e
 *  obrigava metade da aplicação a saber de um formato que ainda vai
 *  mudar. Fica como a nuvem de pontos: um objecto seu na cena, com o
 *  seu painel, que se abre e se fecha sem tocar em mais nada.
 *
 *  O que isto NÃO faz, e convém ficar escrito: não há selecção, não
 *  entra na comparação, no IDS, no BCF nem nos conflitos. Desenha e
 *  mostra o que o ficheiro diz. É o primeiro passo, não o último.
 */

export interface EntradaIfcx {
  id: string
  /** O nome do ficheiro base. */
  ficheiro: string
  /** Os nomes das camadas acrescentadas por cima, por ordem. */
  camadas: string[]
  cena: CenaIfcx
  objecto: THREE.Group
  /** O ficheiro base e as camadas, por ordem. Guarda-se para se poder
   *  recompor tudo do princípio quando chega mais uma: a composição é
   *  barata e uma camada pode mudar qualquer coisa, incluindo apagar
   *  ligações, por isso aplicar só a diferença seria mais frágil e não
   *  mais rápido. */
  baseECamadas: FicheiroIfcx[]
}

const entradas = new Map<string, EntradaIfcx>()
const ouvintes = new Set<() => void>()
let instantaneo: EntradaIfcx[] = []

/** O que está seleccionado, se estiver. É do IFC5 e só dele: não entra
 *  na selecção do resto da aplicação, que fala de `{modelId, localId}`
 *  de Fragments e não saberia o que fazer com um caminho de nó. */
let seleccao: { entradaId: string; path: string } | null = null

function avisar(): void {
  instantaneo = [...entradas.values()]
  for (const o of ouvintes) o()
}

/** Como se vê o que está seleccionado. Não se troca o material, que é
 *  partilhado entre malhas: acende-se a emissão da que foi picada e
 *  apaga-se depois. */
const REALCE = new THREE.Color(0x2e6fd8)
let realcado: THREE.Mesh | null = null
let emissaoAnterior: THREE.Color | null = null

function acender(malha: THREE.Mesh | null): void {
  if (realcado && emissaoAnterior) {
    const m = realcado.material as THREE.MeshLambertMaterial
    m.emissive.copy(emissaoAnterior)
    m.needsUpdate = true
  }
  realcado = null
  emissaoAnterior = null
  if (!malha) return
  const m = malha.material as THREE.MeshLambertMaterial
  if (!m.emissive) return
  emissaoAnterior = m.emissive.clone()
  m.emissive.copy(REALCE)
  m.needsUpdate = true
  realcado = malha
}

export const IFCX_EXTENSOES = ['.ifcx']

export function pareceFicheiroIfcx(nome: string): boolean {
  return /\.ifcx$/i.test(nome)
}

async function lerFicheiro(f: File): Promise<FicheiroIfcx> {
  let bruto: unknown
  try {
    bruto = JSON.parse(await readFileText(f))
  } catch {
    throw new Error(`"${f.name}" não é JSON válido.`)
  }
  if (!pareceIfcx(bruto)) {
    throw new Error(
      `"${f.name}" não parece um IFC5: falta-lhe o cabeçalho com ifcxVersion, ou a lista de dados.`,
    )
  }
  return bruto
}

/** A raiz do projecto, entre as várias que um ficheiro tem.
 *
 *  Um `.ifcx` costuma ter mais do que um nó sem pai: um é o projecto e
 *  os outros são tipos e materiais, que existem para serem herdados e
 *  não para serem desenhados. Desenhá-los todos punha as janelas
 *  soltas na origem, por cima do edifício. */
function raizDoProjecto(
  nos: ReturnType<typeof compor>,
): string | undefined {
  const candidatas = raizes(nos)
  const comFilhos = candidatas.filter((p) => (nos.get(p)?.children.size ?? 0) > 0)
  const comProjecto = comFilhos.find((p) =>
    [...(nos.get(p)?.children.keys() ?? [])].some((k) => /project/i.test(k)),
  )
  return comProjecto ?? comFilhos[0] ?? candidatas[0]
}

function materialDe(m: Malha): THREE.Material {
  const cor = m.cor
    ? new THREE.Color(m.cor[0], m.cor[1], m.cor[2])
    : new THREE.Color(0.72, 0.74, 0.78)
  const opacidade = m.opacidade ?? 1
  return new THREE.MeshLambertMaterial({
    color: cor,
    transparent: opacidade < 1,
    opacity: opacidade,
    // As malhas dos exemplos vêm com faces em ambos os sentidos e sem
    // normais coerentes. Desenhar os dois lados é mais honesto do que
    // ter paredes que desaparecem conforme o ângulo.
    side: THREE.DoubleSide,
  })
}

/** As curvas. São eixos de grelha e linhas de referência, não são
 *  sólidos: desenham-se finas e apagadas, para servirem de apoio sem
 *  competirem com o edifício. */
const MATERIAL_CURVA = new THREE.LineBasicMaterial({ color: 0x7c8598 })

function grupoDe(cena: CenaIfcx, nome: string): THREE.Group {
  const grupo = new THREE.Group()
  grupo.name = `vn2r-ifcx:${nome}`
  for (const el of cena.elementos) {
    if (!el.visivel) continue
    for (const m of el.malhas) {
      const g = new THREE.BufferGeometry()
      g.setAttribute('position', new THREE.BufferAttribute(m.posicoes, 3))
      g.setIndex(new THREE.BufferAttribute(m.indices, 1))
      g.computeVertexNormals()
      const malha = new THREE.Mesh(g, materialDe(m))
      malha.name = el.caminho || el.path
      malha.userData.ifcxPath = el.path
      malha.userData.ifcxClasse = el.classe
      grupo.add(malha)
    }
    for (const c of el.curvas) {
      const g = new THREE.BufferGeometry()
      g.setAttribute('position', new THREE.BufferAttribute(c.posicoes, 3))
      const linha = new THREE.Line(g, MATERIAL_CURVA)
      linha.name = el.caminho || el.path
      linha.userData.ifcxPath = el.path
      grupo.add(linha)
    }
  }
  return grupo
}

function desfazer(grupo: THREE.Group): void {
  grupo.removeFromParent()
  grupo.traverse((o) => {
    // As curvas (eixos, linhas de referência) também têm geometria na
    // placa gráfica. O material delas é partilhado (MATERIAL_CURVA) e
    // fica.
    if (o instanceof THREE.Line) {
      o.geometry.dispose()
      return
    }
    if (!(o instanceof THREE.Mesh)) return
    o.geometry.dispose()
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const mat of mats) mat.dispose()
  })
}

export const ifcxApi = {
  subscribe(ouvinte: () => void): () => void {
    ouvintes.add(ouvinte)
    return () => ouvintes.delete(ouvinte)
  },

  lista(): EntradaIfcx[] {
    return instantaneo
  },

  async abrir(f: File): Promise<EntradaIfcx> {
    const mundo = viewerApi.getWorld()
    if (!mundo) throw new Error('O viewer ainda não está pronto.')
    const ficheiro = await lerFicheiro(f)
    const nos = compor([ficheiro])
    const cena = lerCena(nos, raizDoProjecto(nos))
    const objecto = grupoDe(cena, f.name)
    const entrada: EntradaIfcx = {
      id: crypto.randomUUID(),
      ficheiro: f.name,
      camadas: [],
      cena,
      objecto,
      baseECamadas: [ficheiro],
    }
    entradas.set(entrada.id, entrada)
    mundo.scene.three.add(objecto)
    viewerApi._invalidate()
    avisar()
    return entrada
  },

  /**
   * Acrescenta uma camada por cima de um ficheiro já aberto.
   *
   * É esta a graça do formato: um ficheiro de meio kilobyte, entregue
   * semanas depois, que acrescenta a resistência ao fogo a uma parede
   * sem tocar no modelo. Recompõe-se tudo do princípio, porque a
   * composição é barata e porque uma camada pode mudar qualquer coisa,
   * incluindo apagar ligações.
   */
  async acrescentarCamada(id: string, f: File): Promise<EntradaIfcx> {
    const entrada = entradas.get(id)
    if (!entrada) throw new Error('Esse IFC5 já não está aberto.')
    const mundo = viewerApi.getWorld()
    if (!mundo) throw new Error('O viewer ainda não está pronto.')

    const camada = await lerFicheiro(f)
    const todos = [...entrada.baseECamadas, camada]
    const nos = compor(todos)
    const cena = lerCena(nos, raizDoProjecto(nos))

    // O grupo antigo vai-se embora e com ele a malha realçada: se o
    // ponteiro ficasse, apagar o realce mexia num material já deitado
    // fora.
    acender(null)
    desfazer(entrada.objecto)
    const objecto = grupoDe(cena, entrada.ficheiro)
    entrada.objecto = objecto
    entrada.cena = cena
    entrada.camadas = [...entrada.camadas, f.name]
    entrada.baseECamadas = todos
    mundo.scene.three.add(objecto)
    viewerApi._invalidate()
    avisar()
    return entrada
  },

  /** O nó seleccionado, se houver. */
  seleccionado(): { entradaId: string; path: string } | null {
    return seleccao
  },

  /**
   * Procura um nó IFC5 debaixo do cursor.
   *
   * Raio próprio, e não o do resto da aplicação: as malhas do IFC5 são
   * `THREE.Mesh` normais, com os vértices todos lá, portanto o
   * raycaster do three.js serve. O dos Fragments não, porque fala de
   * geometria instanciada e carregada a pedaços.
   *
   * Devolve true quando apanhou alguma coisa, para quem chamou saber
   * se ainda deve limpar a selecção dos modelos.
   */
  picar(clientX: number, clientY: number, canvas: HTMLCanvasElement): boolean {
    const mundo = viewerApi.getWorld()
    if (!mundo || entradas.size === 0) return false
    const r = canvas.getBoundingClientRect()
    const rato = new THREE.Vector2(
      ((clientX - r.left) / r.width) * 2 - 1,
      -((clientY - r.top) / r.height) * 2 + 1,
    )
    const raio = new THREE.Raycaster()
    raio.setFromCamera(rato, mundo.camera.three as THREE.Camera)
    const grupos = [...entradas.values()].map((e) => e.objecto)
    const toques = raio.intersectObjects(grupos, true)
    const primeiro = toques.find(
      (t) => t.object instanceof THREE.Mesh && t.object.userData.ifcxPath,
    )
    if (!primeiro) {
      ifcxApi.limparSeleccao()
      return false
    }
    const malha = primeiro.object as THREE.Mesh
    const picado = String(malha.userData.ifcxPath)
    const dona = [...entradas.values()].find((e) =>
      e.objecto.children.includes(malha),
    )
    // Sobe do `Body` para o elemento: quem clicou numa parede quer a
    // parede, não o nó que por acaso guarda a malha dela.
    const elemento = dona ? elementoDoNo(dona.cena, picado) : null
    seleccao = { entradaId: dona?.id ?? '', path: elemento?.path ?? picado }
    acender(malha)
    viewerApi._invalidate()
    avisar()
    return true
  },

  limparSeleccao(): void {
    if (!seleccao && !realcado) return
    seleccao = null
    acender(null)
    viewerApi._invalidate()
    avisar()
  },

  fechar(id: string): void {
    const entrada = entradas.get(id)
    if (!entrada) return
    if (seleccao?.entradaId === id) {
      seleccao = null
      acender(null)
    }
    desfazer(entrada.objecto)
    entradas.delete(id)
    viewerApi._invalidate()
    avisar()
  },

  fecharTudo(): void {
    for (const id of [...entradas.keys()]) ifcxApi.fechar(id)
  },

  /** Enquadra a câmara no que está desenhado. */
  enquadrar(id: string): void {
    const entrada = entradas.get(id)
    const mundo = viewerApi.getWorld()
    if (!entrada || !mundo) return
    const caixa = new THREE.Box3().setFromObject(entrada.objecto)
    if (caixa.isEmpty()) return
    void mundo.camera.controls?.fitToBox(caixa, true)
    viewerApi._invalidate()
  },
}
